import type {
  DuplicateCluster,
  NameKind,
  NameOccurrence,
  SyntaxApplyResult,
  SyntaxFix,
  SyntaxSuggestion,
} from '../../../api/endpoints';
import type { ApplyOutcome, FloorPlanGroup, SyntaxRow } from './types';

export const baseName = (file: string): string => file.split('/').pop() || file;

const plural = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

// The one sentence the banner shows after an apply, plus the details a partial
// apply has to own up to.
export const summarizeApply = (result: SyntaxApplyResult): ApplyOutcome => {
  const renamedCount = result.renamed.group + result.renamed.cable_type;
  const planCount = result.pages_touched.length;
  const skipped = result.bb_skipped;
  const errors = result.errors;
  const skippedNote = skipped.length > 0 ? ` · ${skipped.length} not synced` : '';
  // A rename that collides with an existing name folds the two groups into one,
  // pooling their markers — that CHANGES a quantity, so never leave it unsaid.
  const mergedNote = result.merged > 0 ? ` · ${plural(result.merged, 'group')} merged` : '';
  // Nothing landed — don't send the reviewer off to re-match an unchanged takeoff.
  const message =
    planCount === 0
      ? `Nothing was applied${skippedNote}`
      : `Renamed ${plural(renamedCount, 'name')} on ${plural(planCount, 'page')}` +
        `${mergedNote}${skippedNote} · Please re-run Match All`;
  return {
    tone: planCount > 0 && skipped.length === 0 && errors.length === 0 ? 'success' : 'partial',
    message,
    skipped,
    errors,
    planCount,
  };
};

export const describePlans = (occurrences: NameOccurrence[]): string => {
  const files = Array.from(new Set(occurrences.map((occurrence) => baseName(occurrence.file))));
  return files.length <= 3 ? files.join(', ') : `${files.length} floor plans`;
};

export const kindLabel = (kind: NameKind): string => (kind === 'group' ? 'Group' : 'Cable');

export const duplicateMemberKey = (clusterIndex: number, memberIndex: number): string =>
  `cluster-${clusterIndex}-member-${memberIndex}`;

// Expand each suggestion (one per distinct name) into one row per occurrence, so
// the reviewer can accept a fix on some floor plans and leave it on others.
export const expandSuggestions = (suggestions: SyntaxSuggestion[]): SyntaxRow[] => {
  const rows: SyntaxRow[] = [];
  suggestions.forEach((suggestion, suggestionIndex) => {
    suggestion.occurrences.forEach((occurrence, occurrenceIndex) => {
      rows.push({
        key: `suggestion-${suggestionIndex}-${occurrenceIndex}`,
        kind: suggestion.kind,
        original: suggestion.original,
        suggested: suggestion.suggested,
        file: occurrence.file,
        page: occurrence.page,
        reason: suggestion.reason,
      });
    });
  });
  return rows;
};

export const groupRowsByFloorPlan = (rows: SyntaxRow[]): FloorPlanGroup<SyntaxRow>[] => {
  const rowsByFile = new Map<string, SyntaxRow[]>();
  for (const row of rows) {
    const existing = rowsByFile.get(row.file);
    if (existing) existing.push(row);
    else rowsByFile.set(row.file, [row]);
  }
  return Array.from(rowsByFile.entries())
    .map(([file, fileRows]) => ({
      file,
      label: baseName(file),
      rows: fileRows
        .slice()
        .sort((left, right) => left.page - right.page || left.original.localeCompare(right.original)),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const buildSyntaxFixes = (
  rows: SyntaxRow[],
  acceptedRowKeys: Set<string>,
  editedTargets: Record<string, string>,
): SyntaxFix[] => {
  const fixes: SyntaxFix[] = [];
  for (const row of rows) {
    if (!acceptedRowKeys.has(row.key)) continue;
    const target = (editedTargets[row.key] ?? row.suggested).trim();
    if (!target || target === row.original) continue;
    fixes.push({
      kind: row.kind,
      original: row.original,
      suggested: target,
      occurrences: [{ file: row.file, page: row.page }],
    });
  }
  return fixes;
};

export const canonicalFor = (
  cluster: DuplicateCluster,
  clusterIndex: number,
  editedCanonical: Record<number, string>,
): string => (editedCanonical[clusterIndex] ?? cluster.suggested).trim();

export const buildDuplicateFixes = (
  clusters: DuplicateCluster[],
  acceptedMemberKeys: Set<string>,
  editedCanonical: Record<number, string>,
): SyntaxFix[] => {
  const fixes: SyntaxFix[] = [];
  clusters.forEach((cluster, clusterIndex) => {
    const canonical = canonicalFor(cluster, clusterIndex, editedCanonical);
    if (!canonical) return;
    cluster.members.forEach((member, memberIndex) => {
      if (!acceptedMemberKeys.has(duplicateMemberKey(clusterIndex, memberIndex))) return;
      if (member.name === canonical) return;
      fixes.push({
        kind: cluster.kind,
        original: member.name,
        suggested: canonical,
        occurrences: member.occurrences,
      });
    });
  });
  return fixes;
};
